/**
 * Projekttag-Serien — reine Datums-Generierung.
 *
 * Fachlich: PA-Termine folgen einem festen Rhythmus (EBG: Dienstag wöchentlich,
 * Donnerstag 14-tägig, CLAUDE.md §2). Statt jeden Termin einzeln anzulegen,
 * erzeugt `generateSeriesDates` die komplette Terminliste für einen Zeitraum;
 * Ferien und Feiertage werden danach im UI per Checkbox abgewählt (das Modell
 * kennt keinen Ferienkalender — bewusste Entscheidung, siehe unten).
 *
 * Diese Datei ist PURE (CLAUDE.md §26): keine DB, keine Date-„jetzt"-Quelle.
 * Ein- und Ausgabe sind ausschließlich "YYYY-MM-DD"-Strings.
 *
 * TZ-Sicherheit (CLAUDE.md §10): die Iteration rechnet ausschließlich auf
 * UTC-Mitternachten und addiert exakte Vielfache von 24h. Weil beide Endpunkte
 * UTC sind, gibt es keinen DST-Sprung — die Sommerzeit-Umstellung verschiebt
 * sonst den Wochentag, wenn man über lokale Mitternachten iteriert. Die
 * Wallclock-Zeit eines Projekttags steckt ohnehin nicht im Datum, sondern in
 * hourStart/hourEnd über das SchoolHour-Raster.
 *
 * Kein Ferienkalender: NRW-Ferien und bewegliche Feiertage wären ein eigenes
 * Datenmodell mit jährlicher Pflege. Für ~14 abzuwählende Termine pro Schuljahr
 * ist die Checkbox-Liste im Preview der ehrlichere Weg.
 */
/** Rhythmus einer Terminserie. */
export const PROJECT_DAY_SERIES_INTERVALS = ['weekly', 'biweekly'];
export const PROJECT_DAY_SERIES_INTERVAL_LABELS = {
    weekly: 'wöchentlich',
    biweekly: '14-tägig',
};
/** Schrittweite in Tagen je Rhythmus. */
const INTERVAL_DAYS = {
    weekly: 7,
    biweekly: 14,
};
/**
 * Obergrenze für eine einzelne Serie. Ein Schuljahr hat ~40 Wochen, also
 * ~40 wöchentliche Termine; 200 lässt reichlich Luft und deckelt zugleich
 * Tippfehler im Enddatum (z.B. Jahreszahl 2126) auf eine harmlose Größe.
 */
export const PROJECT_DAY_SERIES_MAX_DATES = 200;
const MS_PER_DAY = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Parst "YYYY-MM-DD" zu einem UTC-Mitternacht-Timestamp.
 *
 * Bewusst NICHT `new Date(str)` (CLAUDE.md §10) und mit Round-Trip-Prüfung,
 * damit Kalender-Unsinn wie "2026-02-30" laut scheitert statt still auf den
 * 2. März zu rollen.
 */
function parseDateStr(value, label) {
    const m = DATE_RE.exec(value);
    if (!m) {
        throw new Error(`generateSeriesDates: ${label} "${value}" ist kein YYYY-MM-DD-Datum.`);
    }
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const ms = Date.UTC(year, month - 1, day);
    const d = new Date(ms);
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
        throw new Error(`generateSeriesDates: ${label} "${value}" ist kein gültiges Kalenderdatum.`);
    }
    return ms;
}
/** Formatiert einen UTC-Mitternacht-Timestamp zurück zu "YYYY-MM-DD". */
function formatUtcMs(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/** ISO-8601-Wochentag (Mo=1 … So=7) — passend zu WEEKDAYS_DE in weekdays.ts. */
function isoWeekday(ms) {
    const dow = new Date(ms).getUTCDay(); // 0=So … 6=Sa
    return dow === 0 ? 7 : dow;
}
/**
 * Erzeugt alle Termine des gewählten Wochentags zwischen `from` und `to`
 * (beide inklusive) im gewünschten Rhythmus, aufsteigend sortiert.
 *
 * Der Anker ist das ERSTE Vorkommen des Wochentags ab `from` — bei 14-tägigem
 * Rhythmus zählt also `from` als Referenzwoche. Wer die andere Woche will,
 * setzt `from` eine Woche später.
 *
 * Liefert `[]`, wenn `to` vor `from` liegt oder der Wochentag im Zeitraum
 * nicht vorkommt. Wirft bei ungültigem Datum, Wochentag oder wenn die Serie
 * PROJECT_DAY_SERIES_MAX_DATES überschreiten würde.
 */
export function generateSeriesDates(input) {
    const { from, to, weekday, interval } = input;
    if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
        throw new Error(`generateSeriesDates: weekday ${weekday} außerhalb 1–7 (ISO, Mo=1).`);
    }
    if (!PROJECT_DAY_SERIES_INTERVALS.includes(interval)) {
        throw new Error(`generateSeriesDates: unbekannter Rhythmus "${interval}".`);
    }
    const fromMs = parseDateStr(from, 'from');
    const toMs = parseDateStr(to, 'to');
    if (toMs < fromMs)
        return [];
    // Vom Startdatum auf das erste Vorkommen des Wochentags vorrücken (0–6 Tage).
    const offsetDays = (weekday - isoWeekday(fromMs) + 7) % 7;
    const stepMs = INTERVAL_DAYS[interval] * MS_PER_DAY;
    const dates = [];
    for (let ms = fromMs + offsetDays * MS_PER_DAY; ms <= toMs; ms += stepMs) {
        if (dates.length >= PROJECT_DAY_SERIES_MAX_DATES) {
            throw new Error(`generateSeriesDates: Serie überschreitet ${PROJECT_DAY_SERIES_MAX_DATES} Termine — Zeitraum eingrenzen.`);
        }
        dates.push(formatUtcMs(ms));
    }
    return dates;
}
//# sourceMappingURL=project-day-series.js.map